/**
 * Whale Cove Off Grid — Whale Watching page
 *
 * Four sections, editorial voice:
 *   1. Hero + intro
 *   2. Season tracker (bar chart, current month highlighted)
 *   3. Watching From Shore (tips, hero section — most editorial)
 *   4. Recent Sightings (merged admin + iNaturalist feed)
 *   5. Tour Operators (three hand-picked)
 *   6. Ethics footer
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import axios from "axios";
import { Waves, Eye, Anchor, Shield, ExternalLink, MapPin, Calendar, Check, ArrowRight, Trees, Compass, Tent } from "lucide-react";
import { SILHOUETTE_MAP, LICENSE_NOTE } from "@/components/sections/WhaleSilhouettes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Probability of a sighting by month (0–100) — based on publicly documented
// Bay of Fundy cetacean migration patterns.
const SPECIES = [
  {
    name: "Humpback Whale",
    latin: "Megaptera novaeangliae",
    blurb: "The showstoppers. Breaching, tail-slapping, 40-foot bodies coming out of the water. Summer residents.",
    months: [0, 0, 0, 5, 25, 65, 90, 95, 85, 60, 20, 0],
  },
  {
    name: "Fin Whale",
    latin: "Balaenoptera physalus",
    blurb: "Second-largest animal on Earth. Long, lean, fast. You'll see the back first, then the dorsal fin rising slowly, then gone.",
    months: [0, 0, 0, 10, 35, 70, 85, 90, 85, 70, 30, 5],
  },
  {
    name: "Minke Whale",
    latin: "Balaenoptera acutorostrata",
    blurb: "Small, curious, often close to boats. A pointed nose that breaks the surface before anything else.",
    months: [0, 0, 5, 30, 65, 85, 90, 90, 85, 75, 40, 10],
  },
  {
    name: "North Atlantic Right Whale",
    latin: "Eubalaena glacialis",
    blurb: "Critically endangered — fewer than 350 left. If you see one, you're witnessing something rare. Report it.",
    months: [0, 0, 0, 10, 30, 50, 55, 50, 40, 25, 10, 0],
  },
  {
    name: "Harbor Porpoise",
    latin: "Phocoena phocoena",
    blurb: "Shy, quiet, small. A puff of air and a quick back — blink and you miss it. The Bay of Fundy is one of their strongholds.",
    months: [10, 10, 20, 40, 70, 85, 90, 90, 85, 70, 40, 20],
  },
  {
    name: "Atlantic White-sided Dolphin",
    latin: "Lagenorhynchus acutus",
    blurb: "Pods of 5 to 50, often leaping clear of the water. Look for the white-and-yellow flash on their sides.",
    months: [5, 5, 10, 20, 45, 75, 90, 90, 80, 60, 30, 10],
  },
];

const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const TOUR_OPERATORS = [
  {
    name: "Mariner Cruises",
    location: "Freeport, Long Island",
    driveTime: "35 min from Whale Cove",
    vessel: "47-ft vessel (up to 50 passengers)",
    season: "Mid-June through mid-October",
    trips: "Three daily departures (approx. 8:30 AM, 12:30 PM, 4:30 PM)",
    duration: "3–3.5 hours",
    notes: "One of the longest-running operators on the Neck. Sightings reported for nearly every trip — humpbacks and finbacks are regular.",
    phone: "(866) 866-8797",
    web: "https://marinercruises.ca",
  },
  {
    name: "Petit Passage Whale Watch",
    location: "Tiverton, Long Island",
    driveTime: "20 min from Whale Cove",
    vessel: "Former lobster boat, intimate small-group trips",
    season: "July through October",
    trips: "Daily departures, weather dependent",
    duration: "3 hours",
    notes: "Family-run, local knowledge runs deep. Captain knows where the whales have been moving that week.",
    phone: "(902) 839-2242",
    web: "https://petitpassagewhalewatch.com",
  },
  {
    name: "Ocean Explorations Zodiac Whale Cruises",
    location: "Tiverton, Long Island",
    driveTime: "20 min from Whale Cove",
    vessel: "12-passenger Zodiac — fast, low to the water, closest approach allowed",
    season: "Late June through September",
    trips: "Up to four daily departures in peak season",
    duration: "2–2.5 hours",
    notes: "For the adrenaline version. You're sitting inches above the water. Bring layers — it gets cold at speed, even in August.",
    phone: "(902) 839-2417",
    web: "https://oceanexplorations.ca",
  },
];

function SeasonTracker() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const currentMonth = new Date().getMonth();

  return (
    <section ref={ref} className="wc-section bg-white border-y border-wc-teal/15" data-testid="whales-season">
      <div className="max-w-6xl mx-auto">
        <header className="max-w-3xl mb-12">
          <p className="font-accent italic text-wc-teal text-base mb-3">The Season</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-4">
            When the whales show up.
          </h2>
          <p className="font-body text-wc-slate text-base sm:text-lg leading-relaxed">
            The Bay of Fundy is one of the great feeding grounds on the east coast. Roughly June through October the whales arrive, settle in, and work. Right now we're in <span className="text-wc-forest font-semibold">{MONTHS_LONG[currentMonth]}</span> — here's what's likely.
          </p>
        </header>

        <div className="space-y-5">
          {SPECIES.map((sp, idx) => {
            const Silhouette = SILHOUETTE_MAP[sp.name];
            return (
            <motion.div
              key={sp.name}
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className="grid grid-cols-12 gap-4 sm:gap-6 items-center"
              data-testid={`species-row-${sp.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="col-span-12 sm:col-span-4">
                {Silhouette && (
                  <div className="text-wc-forest mb-2 w-full max-w-[140px] sm:max-w-[180px]" aria-hidden="true">
                    <Silhouette width="100%" height="auto" />
                  </div>
                )}
                <p className="font-display text-wc-forest text-base sm:text-lg font-semibold leading-tight">
                  {sp.name}
                </p>
                <p className="font-accent italic text-wc-slate text-xs mt-0.5">{sp.latin}</p>
                <p className="font-body text-wc-slate text-xs sm:text-sm mt-1.5 leading-relaxed hidden sm:block">
                  {sp.blurb}
                </p>
              </div>
              <div className="col-span-12 sm:col-span-8">
                <div className="grid grid-cols-12 gap-[3px]">
                  {sp.months.map((probability, monthIdx) => {
                    const isCurrent = monthIdx === currentMonth;
                    const h = Math.max(5, probability * 0.32);
                    const op = Math.max(0.15, probability / 100);
                    return (
                      <div key={monthIdx} className="flex flex-col items-center">
                        <div className="w-full relative h-9 flex items-end">
                          <div
                            className={`w-full rounded-[2px] ${isCurrent ? "bg-wc-teal" : "bg-wc-forest"}`}
                            style={{ height: `${h}px`, opacity: op }}
                          />
                        </div>
                        <p className={`font-body text-[9px] mt-1 ${isCurrent ? "text-wc-teal font-bold" : "text-wc-slate/60"}`}>
                          {MONTHS[monthIdx]}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
            );
          })}
        </div>

        <p className="font-accent italic text-wc-slate text-xs sm:text-sm text-center mt-10 opacity-75 max-w-xl mx-auto">
          Bar height = probability of sightings during a given month, based on Bay of Fundy migration patterns. The current month is highlighted.
        </p>
        <p className="font-body text-wc-slate/60 text-[11px] text-center mt-3 opacity-85">
          Silhouettes by <a href={LICENSE_NOTE.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline decoration-wc-teal/30 hover:decoration-wc-teal">{LICENSE_NOTE.author}</a> via {LICENSE_NOTE.source}, licensed under <a href={LICENSE_NOTE.licenseUrl} target="_blank" rel="noopener noreferrer" className="underline decoration-wc-teal/30 hover:decoration-wc-teal">{LICENSE_NOTE.license}</a>.
        </p>
      </div>
    </section>
  );
}

function WatchingFromShore() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  const tips = [
    {
      icon: Eye,
      title: "Watch the water, not the waves",
      body: "Whales break the surface for a second or two at a time. What you're looking for is a puff of mist — the blow — that lingers a moment longer than a whitecap. Twenty to forty seconds later, they'll surface again.",
    },
    {
      icon: Calendar,
      title: "Morning and late afternoon are best",
      body: "Wind is calmer, the light's better, and the whales tend to feed closer to shore when the water's undisturbed. Dawn especially — the first hour after sunrise is gold.",
    },
    {
      icon: MapPin,
      title: "From the Viewing Deck",
      body: "The deck sits about 5 minutes from the sites, facing directly west over the Bay. You can see miles in every direction — when the whales come up the Neck, you see them before anyone else does.",
    },
    {
      icon: Waves,
      title: "Bring binoculars, bring patience",
      body: "Ten minutes of watching is a commitment. Thirty minutes is when you start seeing things. Bring a chair, bring a coffee, stay longer than you think you should.",
    },
  ];

  return (
    <section ref={ref} className="wc-section bg-wc-cream" data-testid="whales-shore">
      <div className="max-w-6xl mx-auto">
        <header className="max-w-3xl mb-12">
          <p className="font-accent italic text-wc-teal text-base mb-3">From the Shore</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-4">
            You don't need a boat.
          </h2>
          <p className="font-body text-wc-slate text-base sm:text-lg leading-relaxed">
            Whale watching from a tour boat is worth doing — the close encounters are something else. But the truth is, most days, with a little patience, you can spot them from the shoreline of the property. Here's how to actually see one.
          </p>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {tips.map((tip, i) => (
            <motion.div
              key={tip.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white/70 border border-wc-teal/15 rounded-lg p-5 sm:p-6"
              data-testid={`shore-tip-${i}`}
            >
              <tip.icon className="w-5 h-5 text-wc-teal mb-3" />
              <h3 className="font-display text-wc-forest text-base sm:text-lg font-semibold mb-2">
                {tip.title}
              </h3>
              <p className="font-body text-wc-slate text-sm leading-relaxed">
                {tip.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RecentSightings() {
  const [sightings, setSightings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/whales/sightings`)
      .then((r) => setSightings(r.data.sightings || []))
      .catch(() => setSightings([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="wc-section bg-white border-y border-wc-teal/15" data-testid="whales-sightings">
      <div className="max-w-6xl mx-auto">
        <header className="max-w-3xl mb-10">
          <p className="font-accent italic text-wc-teal text-base mb-3">Recent Sightings</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-4">
            What's been out there lately.
          </h2>
          <p className="font-body text-wc-slate text-base leading-relaxed">
            A live feed of recent, research-grade sightings from Digby Neck and the surrounding Bay of Fundy waters — combining our own field notes with observations logged by the local naturalist community.
          </p>
        </header>

        {loading ? (
          <p className="font-accent italic text-wc-slate">Loading recent sightings…</p>
        ) : sightings.length === 0 ? (
          <p className="font-accent italic text-wc-slate">
            No recent sightings logged yet — the feed refreshes hourly when new community observations come in.
          </p>
        ) : (
          <ul className="divide-y divide-wc-teal/15 border-y border-wc-teal/15" data-testid="sightings-list">
            {sightings.slice(0, 12).map((s, i) => {
              const d = s.sighted_at ? new Date(s.sighted_at) : null;
              const pretty = d
                ? d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
                : "—";
              return (
                <li key={s.id || i} className="py-4 flex gap-4 items-start" data-testid="sighting-item">
                  <div className="font-body text-wc-slate text-xs tracking-widest uppercase pt-0.5 flex-shrink-0 w-24">
                    {pretty}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-wc-forest text-sm sm:text-base font-semibold">
                      {s.species}
                      {s.count && s.count > 1 && (
                        <span className="font-body text-wc-slate text-xs font-normal ml-2">× {s.count}</span>
                      )}
                    </p>
                    {s.location_hint && (
                      <p className="font-body text-wc-slate text-xs mt-0.5">
                        <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />
                        {s.location_hint}
                      </p>
                    )}
                    {s.notes && (
                      <p className="font-body text-wc-slate text-sm mt-1.5 leading-relaxed line-clamp-2">
                        {s.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="font-body text-[10px] uppercase tracking-widest text-wc-slate/70">
                      {s.source === "admin" ? (s.spotter || "Field note") : "iNaturalist"}
                    </p>
                    {s.source_url && (
                      <a href={s.source_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-body text-wc-teal text-[11px] hover:text-wc-forest mt-0.5">
                        view <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function TourOperators() {
  return (
    <section className="wc-section bg-wc-cream" data-testid="whales-operators">
      <div className="max-w-6xl mx-auto">
        <header className="max-w-3xl mb-12">
          <p className="font-accent italic text-wc-teal text-base mb-3">On the Water</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-4">
            Three tour operators worth knowing about.
          </h2>
          <p className="font-body text-wc-slate text-base leading-relaxed">
            If you want to get out there with them — not just watch from the shore — these are the operators we'd send our own friends to. All based on Long Island, all a short drive through Petit Passage from Whale Cove.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {TOUR_OPERATORS.map((op, i) => (
            <motion.div
              key={op.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex flex-col bg-white/70 border border-wc-teal/15 rounded-lg p-6"
              data-testid={`operator-${op.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <Anchor className="w-5 h-5 text-wc-teal mb-4" />
              <h3 className="font-display text-wc-forest text-xl font-semibold mb-1">{op.name}</h3>
              <p className="font-accent italic text-wc-slate text-sm mb-4">
                {op.location} · {op.driveTime}
              </p>
              <dl className="space-y-2 text-sm font-body text-wc-slate mb-4">
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-wc-slate/70">Vessel</dt>
                  <dd className="text-wc-forest">{op.vessel}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-wc-slate/70">Season</dt>
                  <dd className="text-wc-forest">{op.season}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-wc-slate/70">Departures</dt>
                  <dd className="text-wc-forest">{op.trips}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-widest text-wc-slate/70">Length</dt>
                  <dd className="text-wc-forest">{op.duration}</dd>
                </div>
              </dl>
              <p className="font-body text-wc-slate text-sm leading-relaxed mb-5 flex-1">
                {op.notes}
              </p>
              <div className="flex gap-2 pt-4 border-t border-wc-teal/10 text-xs">
                <a href={op.web} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 font-body text-wc-teal hover:text-wc-forest">
                  Visit site <ExternalLink className="w-3 h-3" />
                </a>
                <span className="text-wc-slate/30">·</span>
                <a href={`tel:${op.phone.replace(/\D/g, "")}`}
                  className="font-body text-wc-slate hover:text-wc-forest">
                  {op.phone}
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanYourVisit() {
  // Internal-link block — gives crawlers strong topical connections between
  // /whales and the rest of the site (Activities, Land, Tides, Waitlist).
  const cards = [
    {
      to: "/activities",
      icon: Compass,
      title: "Activities on Digby Neck",
      desc: "Brier Island ferry, Balancing Rock, dark-sky stargazing, beachcombing — what to do around the property.",
    },
    {
      to: "/land",
      icon: Trees,
      title: "The Land",
      desc: "18.5 acres of Bay of Fundy waterfront across two private off-grid sites — see the property map.",
    },
    {
      to: "/environment",
      icon: Waves,
      title: "Tides & Skies",
      desc: "Live Bay of Fundy tide, wind, moon, and aurora conditions — plan your whale-watching morning.",
    },
    {
      to: "/waitlist",
      icon: Tent,
      title: "Follow the Build",
      desc: "Site One opens for the 2027 season. Stay where the whales swim by.",
    },
  ];
  return (
    <section className="wc-section bg-wc-cream border-t border-wc-driftwood/20" data-testid="whales-related">
      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <p className="font-accent italic text-wc-teal text-base mb-3">Plan your visit</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl font-semibold mb-3">
            Stay where the whales swim by.
          </h2>
          <p className="font-body text-wc-slate text-base max-w-2xl leading-relaxed">
            Whale Cove Off Grid sits directly on the Bay of Fundy on Digby Neck, Nova Scotia — the same waters humpbacks, fins, and minkes feed in from June through October.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <Link
              key={i}
              to={c.to}
              data-testid={`whales-related-${c.to.replace("/", "")}`}
              className="group flex flex-col gap-3 p-5 rounded-md bg-white border border-wc-driftwood/20 hover:border-wc-teal/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-sm bg-wc-teal/10 flex items-center justify-center">
                <c.icon className="w-5 h-5 text-wc-teal" />
              </div>
              <h3 className="font-display text-wc-forest text-base font-semibold group-hover:text-wc-teal transition-colors">{c.title}</h3>
              <p className="font-body text-wc-slate text-sm leading-relaxed">{c.desc}</p>
              <span className="font-body text-wc-teal text-xs inline-flex items-center gap-1 mt-auto">
                Explore <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Ethics() {
  return (
    <section className="wc-section bg-wc-forest text-wc-cream" data-testid="whales-ethics">
      <div className="max-w-3xl mx-auto text-center">
        <Shield className="w-8 h-8 text-wc-teal mx-auto mb-4" />
        <h2 className="font-display text-wc-cream text-2xl sm:text-3xl font-semibold mb-4">
          Watch from a respectful distance.
        </h2>
        <p className="font-body text-wc-cream/85 text-base sm:text-lg leading-relaxed">
          Canadian regulations require staying <span className="font-semibold">100 meters</span> from a whale, and <span className="font-semibold">200 meters</span> from a North Atlantic right whale. Don't chase, don't surround, don't block their path. If you see a whale entangled in fishing gear or injured, call the <span className="font-semibold">Marine Mammal Response Program at 1-866-567-6277</span>. They do serious work and they listen.
        </p>
      </div>
    </section>
  );
}

function GuestSubmission() {
  const [form, setForm] = useState({
    species: "",
    sighted_at: new Date().toISOString().slice(0, 10),
    count: 1,
    location_hint: "",
    notes: "",
    spotter: "",
    spotter_email: "",
  });
  const [state, setState] = useState("idle"); // idle | sending | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/whales/sightings/submit`, {
        ...form,
        count: form.count ? parseInt(form.count, 10) : 1,
      });
      setState("success");
    } catch {
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <section className="wc-section bg-wc-cream border-y border-wc-teal/15" data-testid="whales-submit-success">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 rounded-full bg-wc-teal/15 flex items-center justify-center mx-auto mb-5">
            <Check className="w-7 h-7 text-wc-teal" />
          </div>
          <h2 className="font-display text-wc-forest text-2xl sm:text-3xl font-semibold mb-3">
            Thank you.
          </h2>
          <p className="font-body text-wc-slate text-base leading-relaxed">
            Your sighting has been submitted. We review every one before it goes up — typically within a day or two. If we have questions, we'll reach out to you at the email you provided.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="wc-section bg-wc-cream border-y border-wc-teal/15" data-testid="whales-submit">
      <div className="max-w-3xl mx-auto">
        <header className="mb-8">
          <p className="font-accent italic text-wc-teal text-base mb-3">Saw something?</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-4">
            Add it to the log.
          </h2>
          <p className="font-body text-wc-slate text-base sm:text-lg leading-relaxed">
            If you spotted whales, dolphins, or porpoises from the property or anywhere nearby, we'd love to add your sighting to the page. We review every submission before it's published — just to keep the feed trustworthy.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="bg-white/70 border border-wc-teal/15 rounded-lg p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">What did you see?</label>
              <select
                value={form.species}
                onChange={(e) => setForm({ ...form, species: e.target.value })}
                required
                className="w-full bg-white border border-wc-teal/30 rounded-md px-3 py-2 font-body text-wc-forest text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                data-testid="submit-species"
              >
                <option value="">Choose a species…</option>
                <option value="Humpback Whale">Humpback Whale</option>
                <option value="Fin Whale">Fin Whale</option>
                <option value="Minke Whale">Minke Whale</option>
                <option value="North Atlantic Right Whale">North Atlantic Right Whale</option>
                <option value="Harbor Porpoise">Harbor Porpoise</option>
                <option value="Atlantic White-sided Dolphin">Atlantic White-sided Dolphin</option>
                <option value="Short-beaked Common Dolphin">Short-beaked Common Dolphin</option>
                <option value="Unsure / other cetacean">Unsure / other cetacean</option>
              </select>
            </div>
            <div>
              <label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">When</label>
              <input type="date"
                value={form.sighted_at}
                onChange={(e) => setForm({ ...form, sighted_at: e.target.value })}
                required
                className="w-full bg-white border border-wc-teal/30 rounded-md px-3 py-2 font-body text-wc-forest text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                data-testid="submit-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">How many?</label>
              <input type="number" min="1" max="100"
                value={form.count}
                onChange={(e) => setForm({ ...form, count: e.target.value })}
                className="w-full bg-white border border-wc-teal/30 rounded-md px-3 py-2 font-body text-wc-forest text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                data-testid="submit-count"
              />
            </div>
            <div>
              <label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">Where from?</label>
              <input type="text"
                value={form.location_hint}
                onChange={(e) => setForm({ ...form, location_hint: e.target.value })}
                placeholder="From the Viewing Deck, heading west"
                className="w-full bg-white border border-wc-teal/30 rounded-md px-3 py-2 font-body text-wc-forest text-sm placeholder:text-wc-slate/50 focus:outline-none focus:ring-2 focus:ring-wc-teal"
                data-testid="submit-location"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">What did it look like?</label>
            <textarea rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="A pod of three, feeding on the incoming tide. One full breach, then they worked their way north."
              className="w-full bg-white border border-wc-teal/30 rounded-md px-3 py-2 font-body text-wc-forest text-sm placeholder:text-wc-slate/50 focus:outline-none focus:ring-2 focus:ring-wc-teal resize-none"
              data-testid="submit-notes"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-3 border-t border-wc-teal/10">
            <div>
              <label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">Your name <span className="text-wc-slate/60 normal-case tracking-normal">(optional)</span></label>
              <input type="text"
                value={form.spotter}
                onChange={(e) => setForm({ ...form, spotter: e.target.value })}
                className="w-full bg-white border border-wc-teal/30 rounded-md px-3 py-2 font-body text-wc-forest text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                data-testid="submit-spotter"
              />
            </div>
            <div>
              <label className="font-body text-wc-forest text-xs tracking-widest uppercase mb-1.5 block">Email <span className="text-wc-slate/60 normal-case tracking-normal">(optional)</span></label>
              <input type="email"
                value={form.spotter_email}
                onChange={(e) => setForm({ ...form, spotter_email: e.target.value })}
                placeholder="In case we need to follow up"
                className="w-full bg-white border border-wc-teal/30 rounded-md px-3 py-2 font-body text-wc-forest text-sm placeholder:text-wc-slate/50 focus:outline-none focus:ring-2 focus:ring-wc-teal"
                data-testid="submit-email"
              />
            </div>
          </div>

          {state === "error" && (
            <p className="font-body text-red-600 text-sm">Something went wrong — please try again or email us directly.</p>
          )}

          <div className="flex justify-end">
            <button type="submit" disabled={state === "sending"}
              className="inline-flex items-center gap-2 px-6 py-3 bg-wc-forest text-wc-cream font-body text-sm tracking-wide rounded-full hover:bg-wc-teal transition-colors disabled:opacity-60"
              data-testid="submit-button">
              {state === "sending" ? "Sending…" : "Submit sighting"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default function WhalesPage() {
  return (
    <div className="pt-16 min-h-screen bg-wc-cream" data-testid="whales-page">
      {/* Hero */}
      <section className="wc-section bg-wc-cream pb-0">
        <div className="max-w-5xl mx-auto">
          <p className="font-accent italic text-wc-teal text-base mb-3">Whales</p>
          <h1 className="font-display text-wc-forest text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
            The Bay of Fundy is one of the great feeding grounds on the east coast.
          </h1>
          <p className="font-body text-wc-slate text-base sm:text-lg leading-relaxed max-w-3xl">
            Seven resident species use these waters from summer to fall. Humpbacks, finbacks, minkes — and on rare occasion, one of the last North Atlantic right whales. You can see most of them from a tour boat out of Tiverton. With a little patience, you can sometimes spot them from the shoreline.
          </p>
        </div>
      </section>

      <SeasonTracker />
      <WatchingFromShore />
      <RecentSightings />
      <GuestSubmission />
      <TourOperators />
      <PlanYourVisit />
      <Ethics />
    </div>
  );
}
